<template>
  <p
    class="next-day-shipping text-body-2xs text-center"
    v-html="message"
  />
</template>

<script>

/**
 * Vue: Next day shipping (next-day-shipping)
 * -----------------------------------------------------------------------------
 * Displays next day shipping countdown or fallback text.
 *
 */
/* eslint-disable line-comment-position, no-inline-comments */
import dayjs from 'dayjs'
import isToday from 'dayjs/plugin/isToday'
import isTomorrow from 'dayjs/plugin/isTomorrow'
import { mapState } from 'vuex'

export default {
  name: 'NextDayShipping',

  data() {
    return {
      cutOffDate: {},
      deliveryDate: {},
      now: {},
      remaining: {},
    }
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      markets: (state) => state['next-day-shipping'].markets,
      settings: (state) => state['next-day-shipping'].settings,
    }),

    /**
     * Compute countdown string.
     * @returns {String}
     */
    countdown() {
      const strings = {
        hours: '',
        minutes: '',
        seconds: '',
      }

      Object.keys(strings).forEach((key) => {
        if (!this.remaining[key]) {
          return
        }

        strings[key] = this.$string(`product.next_day_shipping.countdown.${key}`, {
          pluralise: this.remaining[key],
          replace: {
            hours: this.remaining.hours,
            minutes: this.remaining.minutes,
            seconds: this.remaining.seconds,
          },
        })
      })

      /**
       * Removed unused strings.
       */
      if (this.remaining.hours < 1) {
        delete strings.hours
      }

      if (this.remaining.minutes < 1) {
        if (this.remaining.hours < 1) {
          return this.$string('product.next_day_shipping.countdown.less_than_a_minute')
        }

        delete strings.minutes
      }

      if (!this.settings.seconds || this.remaining.seconds < 1) {
        delete strings.seconds
      }

      /**
       * Add delimiters.
       */
      const delimiter = this.$string('product.next_day_shipping.delimiter')
      const delimiterLast = this.$string('product.next_day_shipping.delimiter_last')

      return Object.values(strings).map((string, index) => {
        if (index > 0) {
          if ((index + 1) === Object.values(strings).length) {
            return `${delimiterLast}${string}`
          }

          return `${delimiter}${string}`
        }

        return string
      }).join('')
    },

    /**
     * Compute cut off time.
     * @returns {String}
     */
    cutOffTime() {
      if (!this.market) {
        return ''
      }

      const string = this.market.cutOffTime < 12
        ? 'product.next_day_shipping.morning'
        : 'product.next_day_shipping.afternoon'

      /**
       * Convert 24 hour time to 12 hour time.
       */
      let time = this.market.cutOffTime > 12
        ? this.market.cutOffTime - 12
        : this.market.cutOffTime

      /**
       * Time of 0 is equal to 12am.
       */
      if (this.market?.cutOffTime === 0) {
        time = 12
      }

      return this.$string(string, { replace: { time } })
    },

    /**
     * Compute dayjs date format.
     * @returns {String}
     */
    dateFormat() {
      return this.$string('date_formats.day_month_year')
        .replaceAll('%e', 'D') // D 1..31
        .replaceAll('%d', 'DD') // D 01..31
        .replaceAll('%m', 'MM') // M 01..12
        .replaceAll('%b', 'MMM') // M Jan
        .replaceAll('%B', 'MMMM') // January
        .replaceAll('%y', 'YY') // Y 23
        .replaceAll('%Y', 'YYYY') // Y 2023
    },

    /**
     * Compute if countdown should be displayed.
     * @returns {Boolean}
     */
    enableCountdown() {
      return (
        this.remaining.hours < this.settings.countdown &&
        this.remaining.hours >= 0
      )
    },

    /**
     * Compute excluded dates into dayjs objects.
     * @returns {Array}
     */
    excludedDates() {
      return this.market?.excludedDates.map((date) => {
        return dayjs(date)
      })
    },

    /**
     * Compute matched market.
     * @returns {Object}
     */
    market() {
      return this.markets.find((market) => {
        return market.countries.includes(cnvs.store.country)
      })
    },

    /**
     * Compute message.
     * @returns {String}
     */
    message() {
      if (!this.market) {
        return `
          <a
            class="text-body-2xs-link"
            href="${this.settings.fallbackUrl}"
          >
            ${this.$string('product.next_day_shipping.fallback')}
          </a>
        `
      }

      /**
       * Calculate correct string.
       */
      const base = 'product.next_day_shipping'

      const object = this.cutOffDate.today
        ? 'cut_off_today'
        : 'cut_off_not_today'

      let string = this.enableCountdown
        ? 'countdown'
        : 'time'

      if (!this.cutOffDate.today) {
        string = this.cutOffDate.tomorrow
          ? 'tomorrow'
          : 'not_tomorrow'

        if (this.enableCountdown) {
          string = 'countdown'
        }
      }

      const payments = `
        <span class="visually-hidden">Klarna ${this.$string('product.next_day_shipping.or')} Clearpay</span>
        <span aria-hidden="true">${cnvs.icons.misc.klarna} ${this.$string('product.next_day_shipping.or')} ${cnvs.icons.misc.clearpay}</span>
      `

      /**
       * Output message.
       */
      return this.$string(
        `${base}.${object}.${string}`,
        {
          replace: {
            countdown: this.countdown,
            country: cnvs.store.countryName,
            cut_off_date: this.cutOffDate.display,
            delivery_date: this.deliveryDate.display,
            hours: this.remaining.hours,
            minutes: this.remaining.minutes,
            payments,
            seconds: this.remaining.seconds,
            time: this.cutOffTime,
          },
        },
      )
    },
  },

  /**
   * Extend dayjs before component creation.
   */
  beforeCreate() {
    dayjs.extend(isToday)
    dayjs.extend(isTomorrow)
  },

  mounted() {
    this.setNow()
    this.setDates()
    this.setTimeRemaining()
    this.startCountdown()
  },

  methods: {

    /**
     * Check if date is an excluded date.
     * @param {Object} date - Day JS date object to test.
     * @returns {Boolean}
     */
    isExcludedDate(date) {
      return this.excludedDates?.find((excludedDate) => {
        return date.isSame(excludedDate, 'day')
      })
    },

    /**
     * Set now dayjs object.
     * - Used to set current time for debugging.
     * @returns {Object}
     */
    setNow() {
      let now = dayjs()

      if (cnvs.settings.currentTime) {
        now = dayjs().set('hour', cnvs.settings.currentTime)
      }

      this.now = now
    },

    /**
     * Set cut off and delivery dates.
     */
    setDates() {
      let cutOffDate = this.now
        .set('hour', this.market?.cutOffTime)
        .set('minute', 0)
        .set('second', 0)

      /**
       * If past cut off today then set starting cut off to tomorrow.
       */
      if (this.now.isAfter(cutOffDate)) {
        cutOffDate = cutOffDate.add(1, 'day')
      }

      /**
       * Check that corresponding delivery date is not excluded.
       */
      let deliveryDate = cutOffDate.add(1, 'day')
      let isExcludedDate = this.isExcludedDate(deliveryDate)

      while (isExcludedDate) {
        deliveryDate = deliveryDate.add(1, 'day')
        isExcludedDate = this.isExcludedDate(deliveryDate)
      }

      this.deliveryDate = {
        dayjs: deliveryDate,
        display: deliveryDate.format(this.dateFormat),
        tomorrow: deliveryDate.isTomorrow(),
      }

      /**
       * Re-calculate cut off date based on delivery date.
       */
      cutOffDate = deliveryDate.subtract(1, 'day')

      this.cutOffDate = {
        dayjs: cutOffDate,
        display: cutOffDate.format(this.dateFormat),
        today: cutOffDate.isToday(),
        tomorrow: cutOffDate.isTomorrow(),
      }
    },

    /**
     * Set time remaining to cut off.
     */
    setTimeRemaining() {
      let seconds = this.cutOffDate.dayjs.diff(this.now, 'seconds')
      const hours = Math.floor(seconds / 3600)
      seconds -= hours * 3600
      const minutes = Math.floor(seconds / 60)
      seconds -= minutes * 60

      this.remaining = {
        hours,
        minutes,
        seconds,
      }
    },

    /**
     * Start countdown interval.
     */
    startCountdown() {
      setInterval(() => {
        this.setNow()
        this.setDates()
        this.setTimeRemaining()
      }, 1000)
    },
  },
}
</script>

<style lang="scss">
@import './next-day-shipping';
</style>
